import { formatUnits } from '@ethersproject/units';
import { sum } from 'app/functions/big-number/calculations';
import { asDecimal, handleParsedError, parseUserInitiatedTransactionError } from '@silo/utils';
import useNotify, { notifyWarning } from 'app/hooks/useNotify';
import useSiloRouter from 'app/hooks/useSiloRouter';
import { Recipe } from 'app/queries/decoratedSilo';
import { useAddTransaction, useSiloWeb3 } from '@silo/web3';
import { FC, useCallback } from 'react';
import { AmountInUSD, RawLiquidityTooltip, useActionFormContext } from '../../common/ActionForm';
import { AssetMenu } from '../../common/ActionForm/AssetMenu';
import { AmountInput } from '../../common/AmountInput';
import { HealthFactor } from '../../common/HealthFactor';
import { MaxAmountInput } from '../../common/MaxAmountInput';
import { FullAmountWarning } from '../FullAmountWarning';
import { getNewLTV } from '../getNewLTV';
import { updateAsset } from '../updateAsset';
import { Indicator } from './Indicator';
import { useDualCollateral } from './useDualCollateral';
import { DoWithdrawals } from './useSubmission';
import { useFinishTransaction } from '../../silo/useFinishTransaction';
import { TermsOfUseModal } from '../../terms-of-use';
import { useQueryTermsOfUseAgreement } from 'app/queries/terms-of-use';
import { useThisDecoratedSilo } from '../../silo';
import { Button, Dots, useBreakpoints, useModalState } from '@silo/ui';

const useDoWithdrawals = (): DoWithdrawals => {
  const { routerWithdraw } = useSiloRouter();
  const addTransaction = useAddTransaction();
  const { notifyError } = useNotify();
  const finishTransaction = useFinishTransaction();

  return useCallback(
    async ({ withdrawals, asset }) => {
      try {
        const { decimals, symbol } = asset;
        const tx = await routerWithdraw(...withdrawals);

        const amount = sum(withdrawals.map(({ amount }) => amount));
        const formattedAmount = asDecimal(formatUnits(amount, decimals));

        addTransaction(tx, `Withdraw ${formattedAmount} ${symbol}`);

        const { status } = await tx.wait();

        if (status === 0) {
          throw new Error(`Transaction ${tx.hash} reverted`);
        }

        finishTransaction(getWithdrawalsRecipe({ asset, withdrawals }));
      } catch (err) {
        console.error('Error on withdraw:,', err);
        const parsed = parseUserInitiatedTransactionError(err);

        const captureAndNotify = (message: string) => {
          notifyError(message);
        };

        const { rethrow } = handleParsedError({
          onWarning: notifyWarning,
          onError: captureAndNotify,
          onUnparsed: captureAndNotify,
        })(parsed);

        // re-throw error to be handled by react-query
        if (rethrow) throw err;
      }
    },
    [routerWithdraw, addTransaction, notifyError, finishTransaction]
  );
};

type GetWithdrawalsRecipe = (...params: Parameters<DoWithdrawals>) => Recipe;

const getWithdrawalsRecipe: GetWithdrawalsRecipe =
  ({ withdrawals, asset: { assetAddress } }) =>
  (draft) => {
    withdrawals.forEach(({ isFullAmount, isCollateralOnly = false, amount }) => {
      updateAsset({
        draft,
        assetAddress,
        isFullAmount,
        isProtected: isCollateralOnly,
        amount,
      });
    });
  };

interface DualCollateralProps {
  'data-cy'?: string;
}

export const DualCollateral: FC<DualCollateralProps> = ({ 'data-cy': dataCy }) => {
  const { assetMenuAssets, selectedAddress, setSelectedAddress, selectedAsset, resetTokenAmount } =
    useActionFormContext();
  const { symbol, decimals } = selectedAsset;

  const { protocolInstance, account } = useSiloWeb3();
  const doWithdrawals = useDoWithdrawals();
  const { sm } = useBreakpoints();

  const {
    handleSubmit,
    lendingAmount,
    setLendingAmount,
    lendingAmountRef,
    normalizedLendingAmount,
    normalizedProtectedAmount,
    normalizedTotalAmount,
    protectedAmount,
    setProtectedAmount,
    maxLendingAmount,
    maxProtectedAmount,
    doLendingMax,
    doProtectedMax,
    isFullLendingAmount,
    isFullProtectedAmount,
    currentLTV,
    remainingAmount,
    lendingError,
    protectedError,
    formError,
    isInvalid,
    isPending,
  } = useDualCollateral({ selectedAsset, doWithdrawals });

  const { isModalOpen, closeModal, openModal } = useModalState();
  const { hasAgreed } = useQueryTermsOfUseAgreement();
  const { siloAddress } = useThisDecoratedSilo();

  if (!protocolInstance) return null;

  const highlightLendingInput = lendingError || formError;
  const highlightProtectedInput = protectedError || formError;
  const disableSubmit = isInvalid || isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        if (hasAgreed) {
          handleSubmit();
        } else {
          openModal();
        }
      }}
      className="flex flex-col gap-5"
    >
      {!sm ? (
        <AssetMenu
          protocolInstance={protocolInstance}
          assets={assetMenuAssets}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          onSelect={resetTokenAmount}
          disabled={isPending}
          dataCyPrefix={dataCy}
          asButton
        />
      ) : (
        <div className="flex gap-6 items-center">
          <h2 className="font-medium text-white">Withdraw from</h2>
          {assetMenuAssets.length > 0 ? (
            <AssetMenu
              protocolInstance={protocolInstance}
              assets={assetMenuAssets}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              onSelect={resetTokenAmount}
              disabled={isPending}
              dataCyPrefix={dataCy}
            />
          ) : null}
        </div>
      )}

      <label className="font-medium text-white" htmlFor="">
        My lending activated {symbol} deposit
      </label>

      <div
        className={`p-2.5 sm:p-5 rounded-lg ${isPending ? 'bg-shale-750' : 'bg-shale-700'} ${
          highlightLendingInput ? 'outline outline-red-400 outline-2' : ''
        }`}
      >
        <div className="flex gap-5 items-center">
          <AmountInput
            inputVal={lendingAmount}
            setInputVal={setLendingAmount}
            decimals={decimals}
            ref={lendingAmountRef}
            disabled={isPending}
          />
          <AmountInUSD selectedAsset={selectedAsset} normalizedAmount={normalizedLendingAmount} disabled={isPending} />
        </div>
      </div>

      {lendingError && (
        <p className="text-base text-red-400" data-cy="form-error-message">
          {lendingError}
        </p>
      )}

      {isFullLendingAmount && <FullAmountWarning />}

      <MaxAmountInput
        symbol={selectedAsset.symbol}
        max={maxLendingAmount}
        doMax={doLendingMax}
        maxLabelSlot={
          <div className="flex gap-2 items-center">
            <span>Available to withdraw</span>
            {maxLendingAmount.type === 'liquidity' && (
              <RawLiquidityTooltip maxAmount={maxLendingAmount.value} selectedAsset={selectedAsset} action="withdraw" />
            )}
          </div>
        }
        disabled={isPending}
      />

      <div className="h-px bg-shale-600"></div>

      <label className="font-medium text-white" htmlFor="">
        My lending deactivated {symbol} deposit
      </label>

      <div
        className={`p-2.5 sm:p-5 rounded-lg ${isPending ? 'bg-shale-750' : 'bg-shale-700'} ${
          highlightProtectedInput ? 'outline outline-red-400 outline-2' : ''
        }`}
      >
        <div className="flex gap-5 items-center">
          <AmountInput
            inputVal={protectedAmount}
            setInputVal={setProtectedAmount}
            decimals={decimals}
            disabled={isPending}
          />
          <AmountInUSD
            selectedAsset={selectedAsset}
            normalizedAmount={normalizedProtectedAmount}
            disabled={isPending}
          />
        </div>
      </div>

      {protectedError && (
        <p className="mt-1 text-sm text-red-400" data-cy="form-error-message">
          {protectedError}
        </p>
      )}

      {isFullProtectedAmount && <FullAmountWarning />}

      <MaxAmountInput
        symbol={selectedAsset.symbol}
        max={maxProtectedAmount}
        doMax={doProtectedMax}
        maxLabelSlot={<span>Available to withdraw</span>}
        disabled={isPending}
      />

      {currentLTV && (
        <div>
          <HealthFactor
            variant="staticDual"
            normalizedTokenAmount={normalizedTotalAmount}
            currentLTV={currentLTV}
            getNewLTV={getNewLTV}
            selectedAsset={selectedAsset}
          />
        </div>
      )}

      <Indicator formError={formError} remainingAmount={remainingAmount} selectedAsset={selectedAsset} />

      <Button variant="primary" type="submit" disabled={disableSubmit}>
        {isPending ? <Dots>Withdrawing</Dots> : 'Withdraw'}
      </Button>

      {!!account && (
        <TermsOfUseModal
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          user={account}
          onSuccess={() => {
            handleSubmit();
          }}
          market={siloAddress}
          action="withdraw"
          protocolInstance={protocolInstance}
        />
      )}
    </form>
  );
};
